import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelCampaignRegistrationStatuses, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getHealthCheckAutoInviteData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import downloadAsCsv from '../../../utils/downloadAsCsv'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { NoteBatchLoaderProvider, useNoteBatchLoader } from '../Common/NoteBatchLoaderContext'
import { ReactComponent as ExcelIcon } from '../Billing/excel.svg'
import styles from './HealthCheckAutoInvite.module.scss'
import AutoInviteHealthRow from './Row'
import { getSortedResults } from './sortHelper'
import AutoInviteHealthTitleRow from './TitleRow'
import TotalRow from './TotalRow'

const HealthCheckAutoInvite = () => {
  const dispatch = useDispatch()

  const isLoading = useSelector(s => s.healthCheck.invites.isLoading)
  const hotelData = useSelector(s => s.healthCheck.invites.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.invites.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.invites.hotelChains)

  const [filterState, filterDispatch] = useHealthCheckFilters('auto-invite')
  const [sortDecreasing, setSortDecreasing] = useState(false)
  const [sortByIndex, setSortByIndex] = useState(3)
  const [results, setResults] = useState([])

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState

  // Calculate row status for AutoInvite tab
  const calculateRowStatus = row => {
    const { invited, bookings, conversions, marketingCampaigns } = row
    const inviteCount = invited?.periodCurrent || 0
    const invitePrevious = invited?.periodPrevious || 0
    const bookingCount = bookings?.periodCurrent || 0
    const conversionCount = conversions?.periodCurrent || 0

    // Check for significant reduction in invites (> 30% drop)
    const hasSignificantReduction =
      invitePrevious > 0 &&
      inviteCount < invitePrevious &&
      (invitePrevious - inviteCount) / invitePrevious > 0.3

    // Calculate disabled campaigns percentage (3 Mo, 6 Mo, 9 Mo, 12 Mo, 15 Mo, 15 Mo+, After visit)
    const inviteStatusCampaignCategories = [
      'Lost3Months',
      'Lost6Months',
      'Lost9Months',
      'Lost12Months',
      'Lost15Months',
      'Lost15MonthsPlus',
      'AfterLastVisit'
    ]

    let disabledCount = 0
    let totalCampaigns = 0
    if (marketingCampaigns && marketingCampaigns.length > 0) {
      inviteStatusCampaignCategories.forEach(category => {
        const campaign = marketingCampaigns.find(c => c.category === category)
        if (campaign) {
          totalCampaigns++
          if (!campaign.isActive) {
            disabledCount++
          }
        }
      })
    }

    const disabledPercentage = totalCampaigns > 0 ? (disabledCount / totalCampaigns) * 100 : 0

    // ALERT conditions:
    // 1. No invites sent
    if (inviteCount === 0) {
      return 'alert'
    }

    // 2. Significant reduction in invites (> 30% drop)
    if (hasSignificantReduction) {
      return 'alert'
    }

    // 3. No bookings or conversions despite having invites
    if (inviteCount > 0 && bookingCount === 0 && conversionCount === 0) {
      return 'alert'
    }

    // 4. More than 50% of invite campaigns are disabled
    if (disabledPercentage > 50) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. Low invites (< 10)
    if (inviteCount > 0 && inviteCount < 10) {
      return 'warning'
    }

    // 2. Low conversion rate (< 5% if invites > 10)
    if (inviteCount >= 10 && conversionCount > 0) {
      const conversionRate = conversionCount / inviteCount
      if (conversionRate < 0.05) {
        return 'warning'
      }
    }

    // 3. 50-80% of invite campaigns are disabled
    if (disabledPercentage >= 50 && disabledPercentage <= 80) {
      return 'warning'
    }

    // GREEN conditions:
    // Good invite count and reasonable conversion rate, and < 50% disabled
    if (disabledPercentage < 50) {
      if (inviteCount >= 10 && conversionCount > 0) {
        const conversionRate = conversionCount / inviteCount
        if (conversionRate >= 0.05) {
          return 'green'
        }
      }
      // If < 50% disabled, still green even if conversion rate is lower
      if (inviteCount >= 10) {
        return 'green'
      }
    }

    // Default to warning if status is unclear
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const { invited, bookings, conversions, marketingCampaigns } = row
    const inviteCount = invited?.periodCurrent || 0
    const invitePrevious = invited?.periodPrevious || 0
    const bookingCount = bookings?.periodCurrent || 0
    const conversionCount = conversions?.periodCurrent || 0

    const hasSignificantReduction =
      invitePrevious > 0 &&
      inviteCount < invitePrevious &&
      (invitePrevious - inviteCount) / invitePrevious > 0.3

    const inviteStatusCampaignCategories = [
      'Lost3Months',
      'Lost6Months',
      'Lost9Months',
      'Lost12Months',
      'Lost15Months',
      'Lost15MonthsPlus',
      'AfterLastVisit'
    ]

    let disabledCount = 0
    let totalCampaigns = 0
    if (marketingCampaigns && marketingCampaigns.length > 0) {
      inviteStatusCampaignCategories.forEach(category => {
        const campaign = marketingCampaigns.find(c => c.category === category)
        if (campaign) {
          totalCampaigns++
          if (!campaign.isActive) {
            disabledCount++
          }
        }
      })
    }

    const disabledPercentage = totalCampaigns > 0 ? (disabledCount / totalCampaigns) * 100 : 0
    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (inviteCount === 0) {
        reasons.push('No invites sent')
      }
      if (hasSignificantReduction) {
        const reductionPercent = ((invitePrevious - inviteCount) / invitePrevious * 100).toFixed(1)
        reasons.push(`Significant reduction in invites (${reductionPercent}% drop)`)
      }
      if (inviteCount > 0 && bookingCount === 0 && conversionCount === 0) {
        reasons.push('No bookings or conversions despite having invites')
      }
      if (disabledPercentage > 50) {
        reasons.push(`Features enabled: ${(100 - disabledPercentage).toFixed(0)}% (${disabledCount}/${totalCampaigns} disabled, > 50% disabled)`)
      }
    } else if (status === 'warning') {
      if (inviteCount > 0 && inviteCount < 10) {
        reasons.push(`Low invites: ${inviteCount} (< 10)`)
      }
      if (inviteCount >= 10 && conversionCount > 0) {
        const conversionRate = (conversionCount / inviteCount * 100).toFixed(1)
        if (conversionRate < 5) {
          reasons.push(`Low conversion rate: ${conversionRate}% (< 5%)`)
        }
      }
      if (disabledPercentage >= 50 && disabledPercentage <= 80) {
        reasons.push(`Features enabled: ${(100 - disabledPercentage).toFixed(0)}% (${disabledCount}/${totalCampaigns} disabled, 50-80% disabled)`)
      }
    } else if (status === 'green') {
      if (disabledPercentage < 50) {
        reasons.push(`Features enabled: ${(100 - disabledPercentage).toFixed(0)}% (${totalCampaigns - disabledCount}/${totalCampaigns} enabled, < 50% disabled)`)
      }
      if (inviteCount >= 10) {
        reasons.push(`Invites: ${inviteCount}`)
      }
      if (conversionCount > 0) {
        const conversionRate = (conversionCount / inviteCount * 100).toFixed(1)
        reasons.push(`Conversion rate: ${conversionRate}%`)
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  const widths = filterState.showTelcoDetails
    ? [40, 264, 80, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 350]
    : [40, 350, 80, 80, 80, 80, 82, 60, 60, 60, 60, 60, 60, 60, 60, 60, 350]

  useEffect(() => {
    dispatch(getHealthCheckAutoInviteData(filterState.periodStart, filterState.periodEnd))
  }, [filterState.periodEnd, filterState.periodStart]) // Removed dispatch - it's stable and doesn't need to be in deps

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds,
        showLowOutreaches,
        showLowConversions
      } = filterState

      const filtered = hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => (showLowOutreaches === true ? h.bookings.periodCurrent === 0 : true))
        .filter(h =>
          showLowConversions === true ? h.conversions.periodCurrent === 0 : true
        )
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })

      // Apply status filter
      return filtered.filter(h => {
        const status = calculateRowStatus(h)
        if (status === 'alert') return showAlert
        if (status === 'warning') return showWarning
        if (status === 'green') return showGreen
        return true
      })
    }

    function getResults() {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        filterState,
        sortByIndex,
        sortDecreasing
      )
      setResults(results)
    }

    getResults()
  }, [filterState, hotelData, sortByIndex, sortDecreasing, showAlert, showWarning, showGreen])

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }
    setSortByIndex(rowIndex)
  }

  // Prepare subsidiary IDs for batch loading
  const subsidiaryIdsForBatchLoading = useMemo(() => {
    return results.map(h => ({
      subsidiaryId: h.subsidiaryId,
      kind: 'healthCheck-autoinvite'
    }))
  }, [results])

  // Get notes cache for CSV export
  const AutoInviteContent = () => {
    const { notesCache } = useNoteBatchLoader()

    function handleClickDownloadAsCsv() {
      // Get filter values for metadata
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds,
        showTelcoDetails,
        showLowOutreaches,
        showLowConversions,
        periodStart,
        periodEnd
      } = filterState

      // Helper function to get filter label
      const getFilterLabel = (value, allOptions, defaultLabel) => {
        if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
          return 'All'
        }
        if (Array.isArray(value)) {
          return value.map(v => allOptions[v] || v).join(', ')
        }
        return allOptions[value] || value || defaultLabel
      }

      // Get CSM names if available
      const getCsmLabel = () => {
        if (!csmIds || csmIds.length === 0) return 'All'
        return csmIds.join(', ')
      }

      // Get status labels
      const getStatusLabel = () => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
        return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
      }

      const filterMetadata = [
        ['Filter Values Applied:'],
        ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
        ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
        ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
        ['Status', getStatusLabel()],
        ['CSM', getCsmLabel()],
        ['Search', searchString || 'None'],
        ['Show Telco Details', showTelcoDetails ? 'Yes' : 'No'],
        ['Show Low Outreach', showLowOutreaches ? 'Yes' : 'No'],
        ['Show Low Conversions', showLowConversions ? 'Yes' : 'No'],
        ['Period Start', periodStart ? moment(periodStart).format('MM/DD/YYYY') : 'All'],
        ['Period End', periodEnd ? moment(periodEnd).format('MM/DD/YYYY') : 'All'],
        ['Status Filters', `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`],
        [] // Empty row separator
      ]

      const titleRow = filterState.showTelcoDetails
        ? [
            'Hotel ID',
            'Hotel Name',
            'Hotel Group',
            'Brand',
            'Chain',
            'CSM',
            'Status',
            'Click',
            'Text',
            'Booking',
            'Converted',
            'Inbound',
            'Outbound',
            'Optout',
            'Segment outbound',
            'Segment inbound',
            'Failed spam',
            'Failed other',
            'Service Start Date',
            'Date Added',
            'Notes'
          ]
        : [
            'Hotel ID',
            'Hotel Name',
            'Hotel Group',
            'Brand',
            'Chain',
            'CSM',
            'Status',
            'Click',
            'Text',
            'Booking',
            'Converted',
            '3 Mo',
            '6 Mo',
            '9 Mo',
            '12 Mo',
            '15 Mo',
            '15 Mo+',
            'After visit',
            'Service Start Date',
            'Date Added',
            'Notes'
          ]

      const resAsArrays = results.map(h => {
        // Get note from cache if available
        const noteKey = `${h.subsidiaryId}-healthCheck-autoinvite`
        const note = notesCache?.get(noteKey) || '-'

        const baseRow = [
          h.displayId,
          `${h.brand || ''} ${h.shortName || ''}`.trim(),
          h.groupName || '-',
          h.parentChainCode || '-',
          h.chainCode || '-',
          h.csmUserFullName || '-',
          h.subsidiaryStatus ? (hotelStatusNamesFilterable[h.subsidiaryStatus] || h.subsidiaryStatus) : '-'
        ]

        if (filterState.showTelcoDetails) {
          return [
            ...baseRow,
            h.clicks?.periodCurrent || 0,
            h.invited?.periodCurrent || 0,
            h.bookings?.periodCurrent || 0,
            h.conversions?.periodCurrent || 0,
            h.textInbound?.periodCurrent || 0,
            h.textOutbound?.periodCurrent || 0,
            h.optOut?.periodCurrent || 0,
            h.segmentOutbound?.periodCurrent || 0,
            h.segmentInbound?.periodCurrent || 0,
            h.inviteFailedSpam?.periodCurrent || 0,
            h.inviteFailedOther?.periodCurrent || 0,
            h.serviceStartDate
              ? moment(h.serviceStartDate).format('MM/DD/YYYY')
              : '-',
            h.addedOn
              ? moment(h.addedOn).format('MM/DD/YYYY')
              : '-',
            note
          ]
        } else {
          return [
            ...baseRow,
            h.clicks?.periodCurrent || 0,
            h.invited?.periodCurrent || 0,
            h.bookings?.periodCurrent || 0,
            h.conversions?.periodCurrent || 0,
            h.marketingCampaigns?.find(c => c.category === 'Lost3Months')?.isActive ? 'Yes' : 'No',
            h.marketingCampaigns?.find(c => c.category === 'Lost6Months')?.isActive ? 'Yes' : 'No',
            h.marketingCampaigns?.find(c => c.category === 'Lost9Months')?.isActive ? 'Yes' : 'No',
            h.marketingCampaigns?.find(c => c.category === 'Lost12Months')?.isActive ? 'Yes' : 'No',
            h.marketingCampaigns?.find(c => c.category === 'Lost15Months')?.isActive ? 'Yes' : 'No',
            h.marketingCampaigns?.find(c => c.category === 'Lost15MonthsPlus')?.isActive ? 'Yes' : 'No',
            h.marketingCampaigns?.find(c => c.category === 'AfterLastVisit')?.isActive ? 'Yes' : 'No',
            h.serviceStartDate
              ? moment(h.serviceStartDate).format('MM/DD/YYYY')
              : '-',
            h.addedOn
              ? moment(h.addedOn).format('MM/DD/YYYY')
              : '-',
            note
          ]
        }
      })

      // Combine metadata, title row, and data
      const dataArray = [
        ...filterMetadata,
        titleRow,
        ...resAsArrays
      ]
      
      const fileName = `health_check_autoinvite_${moment().format('YYYY-MM-DD')}.csv`

      downloadAsCsv(dataArray, fileName)
    }

    return (
      <>
        <HealthCheckFilters
          state={filterState}
          dispatch={filterDispatch}
          allhotelGroups={allhotelGroups}
          allhotelChains={allhotelChains}
          showTelcoToggle
          showLowOutreachToggle
          showLowConversionsToggle
          showDateFilter
          showSubsidiaryStatus
          showCsmFilter
        >
          <div className={styles.alertStatusControls}>
            <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
            <Toggle
              className={styles.toggle}
              checked={showAlert}
              handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
              label="Alert"
            />
            <Toggle
              className={styles.toggle}
              checked={showWarning}
              handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
              label="Warning"
            />
            <Toggle
              className={styles.toggle}
              checked={showGreen}
              handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
              label="Green"
            />
          </div>
          <SecondaryRegularButton
            className={styles.copyToExcelButton}
            onClick={handleClickDownloadAsCsv}
          >
            <ExcelIcon /> Download as CSV
          </SecondaryRegularButton>
        </HealthCheckFilters>
        <div className={styles.tableContainer}>
          {isLoading ? (
            <LoadingIndicator marginTop={100} />
          ) : (
            <Table
              widths={widths}
              handleTitleCellClick={handleTitleCellClick}
              sortIndex={sortByIndex}
              sortDirection={sortDecreasing ? 'down' : 'up'}
            >
              <AutoInviteHealthTitleRow
                showTelcoDetails={filterState.showTelcoDetails}
                sortByIndex={sortByIndex}
                sortDecreasing={sortDecreasing}
              />

              {results.length === 0 && (
                <div className={styles.noResults}>
                  No matching results. Try modifiying the filters.
                </div>
              )}
              {results.map((h, i) => {
                const rowStatus = calculateRowStatus(h)
                const statusExplanation = getStatusExplanation(h)
                return (
                  <AutoInviteHealthRow
                    {...h}
                    index={i + 1}
                    showTelcoDetails={filterState.showTelcoDetails}
                    rowStatus={rowStatus}
                    statusExplanation={statusExplanation}
                  />
                )
              })}
              <TotalRow showTelcoDetails={filterState.showTelcoDetails} data={results} />
            </Table>
          )}
        </div>
      </>
    )
  }

  return (
    <NoteBatchLoaderProvider
      subsidiaryIds={subsidiaryIdsForBatchLoading}
      kind="healthCheck-autoinvite"
    >
      <div className={styles.wrapper}>
        <CustomHeader title="Hotel health check" width={1450} isFixed />
        <Tab.Parent>
          <Tab to="/healthCheck/pms">PMS</Tab>
          <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
          <Tab to="/healthCheck/billing">Billing</Tab>
          <Tab to="/healthCheck/performance">Performance</Tab>
          <Tab to="/healthCheck/summary">Summary</Tab>
        </Tab.Parent>
        <AutoInviteContent />
    </div>
    </NoteBatchLoaderProvider>
  )
}

export default HealthCheckAutoInvite
